const clean=value=>String(value || '').normalize('NFC').replace(/\s+/g,' ').trim();
export const exampleWordCount=value=>(clean(value).match(/[\p{L}\p{N}][\p{L}\p{M}\p{N}]*(?:['’-][\p{L}\p{M}\p{N}]+)*/gu) || []).length;
const normalized=value=>clean(value).toLowerCase().replace(/[^\p{L}\p{M}\p{N}\s]/gu,'').replace(/\s+/g,' ');
const clip=(text,limit)=>text.length<=limit?text:text.slice(0,limit).replace(/\s+\S*$/,'').trim();
// Match complete cue words in English and the supported teaching languages.
// Unicode boundaries keep Malayalam vowel signs and conjuncts intact.
const cues=/((?<![\p{L}\p{M}\p{N}])(?:(?:for\s+|an?\s+)?example|ഉദാഹരണത്തിന്|ഉദാഹരണമായി|ഉദാഹരണം|എക്സാമ്പിൾ|എക്സാംപിൾ|ഉദാഹരണങ്ങൾ|उदाहरण(?:\s+के\s+लिए)?|मिसाल|எடுத்துக்காட்டாக|எடுத்துக்காட்டு|உதாரணமாக|உதாரணம்|ఉదాహరణకు|ఉదాహరణ|ಉದಾಹರಣೆಗೆ|ಉದಾಹರಣೆ|على\s+سبيل\s+المثال|مثلا|مثلاً|مثال)(?![\p{L}\p{M}\p{N}]))/giu;
export function exampleExplanationStart(text) {
    const value=clean(text);
    for(const match of value.matchAll(cues)) {
        const prefix=value.slice(0,match.index);
        if(/\b(?:not|without|no)\s*(?:an?\s+)?$/i.test(prefix))continue;
        return value.slice(match.index+match[0].length).replace(/^[\s,:;.–—-]+/,'');
    }
    return null;
}
export const hasExampleDetail=text=>exampleWordCount(text)>=6 && clean(text).length>=30;

// Capture starts after a spoken cue. Generate after a pause, using final text
// or a stable interim transcript when the browser does not finalize it.
export function createSpokenLectureExample({onExample,onChange=()=>{},silenceMs=3000,maxDurationMs=60000,minWords=6,manualMinWords=6,maxChars=4000,dedupMs=60000,clock=()=>Date.now()}={}) {
    let active=false,speaking=false,disposed=false,text='',interim='',status='idle',message='',quietTimer,maxTimer,revision=0;
    const completed=new Map();
    const preview=()=>clip(clean([text,interim].filter(Boolean).join(' ')),maxChars);
    const snapshot=()=>({draft:preview(),confirmedText:text,interimText:interim,captureStatus:status,captureMessage:message,wordCount:exampleWordCount(preview()),canGenerate:!disposed && status!=='submitted' && exampleWordCount(preview())>=manualMinWords});
    const notify=()=>{if(!disposed)onChange(snapshot());};
    const clear=()=>{clearTimeout(quietTimer);clearTimeout(maxTimer);};
    function submit(manual=false,override) {
        if(disposed)return false;
        if(manual && override!==undefined && clean(override).length>maxChars) {status='ready';message=`Keep the explanation within ${maxChars} characters before generating.`;notify();return false;}
        const content=manual?clip(override===undefined?preview():clean(override),maxChars):clean(text);
        const minimum=manual?manualMinWords:minWords;
        if(exampleWordCount(content)<minimum || content.length<30) {
            status='needs-detail';message=`Keep explaining the example; at least ${minimum} words of detail are needed.`;notify();return false;
        }
        const key=normalized(content),now=clock();
        for(const [item,time] of completed)if(now-time>=dedupMs)completed.delete(item);
        if(completed.has(key)) {clear();active=false;interim='';text=content;status='submitted';message='This explanation has already been sent.';notify();return false;}
        completed.set(key,now);while(completed.size>20)completed.delete(completed.keys().next().value);
        clear();active=false;speaking=false;interim='';text=content;status='submitted';message='Explanation sent for illustration.';
        const submittedRevision=++revision;notify();
        try {
            Promise.resolve(onExample?.(content)).catch(()=>{
                completed.delete(key);
                if(disposed || revision!==submittedRevision)return;
                status='ready';message='The illustration could not be started. Review the explanation and try Generate now again.';notify();
            });
        } catch {
            completed.delete(key);status='ready';message='The illustration could not be started. Try Generate now again.';notify();
        }
        return true;
    }
    const waitForSilence=()=>{
        clearTimeout(quietTimer);
        if(!active)return;
        quietTimer=setTimeout(()=>{
            if(!active || speaking)return;
            // Engines can leave their last words interim even after speech ends.
            // Use the stable visible transcript after silence instead of waiting
            // indefinitely for a final event that may never arrive.
            if(interim){text=preview();interim='';}
            submit(false);
        },silenceMs);
    };
    function start(initial) {
        clear();revision++;active=true;speaking=false;text=clip(clean(initial),maxChars);interim='';status='listening';message='Listening to the example explanation…';
        if(clean(initial).length>maxChars) {active=false;status='ready';message='The explanation reached its length limit. Review it and use Generate now.';notify();return;}
        maxTimer=setTimeout(()=>{
            if(!active)return;
            clearTimeout(quietTimer);active=false;
            status=exampleWordCount(preview())>=minWords?'ready':'needs-detail';
            message='The capture time limit was reached. Review this explanation and use Generate now, or cancel and start another example.';
            notify();
        },maxDurationMs);
        waitForSilence();notify();
    }
    return {
        getState:snapshot,
        receiveFinal(value) {
            if(disposed)return;
            const final=clean(value);if(!final)return;
            if(!active) {const beginning=exampleExplanationStart(final);if(beginning!==null)start(beginning);return;}
            const beginning=exampleExplanationStart(final);
            const confirmed=!text && beginning!==null?beginning:final;
            const combined=clean([text,confirmed].filter(Boolean).join(' '));
            text=clip(combined,maxChars);interim='';speaking=false;
            status='listening';message='Waiting for you to finish the explanation…';
            if(combined.length>maxChars) {clear();active=false;status='ready';message='The explanation reached its length limit. Review it and use Generate now.';}
            else waitForSilence();
            notify();
        },
        receiveInterim(value) {
            if(disposed)return;
            if(!active){const beginning=exampleExplanationStart(value);if(beginning===null)return;start('');interim=beginning;status='listening';message='Listening to the example explanation…';}
            const incoming=exampleExplanationStart(value);
            const remaining=Math.max(0,maxChars-text.length-1),spoken=clean(incoming!==null && !text?incoming:value);
            if(spoken.length>remaining){interim=clip(spoken,remaining);clear();active=false;status='ready';message='The explanation reached its length limit. Review it and use Generate now.';notify();return;}
            interim=clip(clean(incoming!==null && !text?incoming:value),Math.max(0,maxChars-text.length-1));
            if(interim){speaking=true;clearTimeout(quietTimer);status='listening';message='Listening to the example explanation…';}
            else{speaking=false;waitForSilence();}
            notify();
        },
        speechStart() {if(disposed || !active)return;speaking=true;clearTimeout(quietTimer);},
        speechEnd() {if(disposed || !active)return;speaking=false;waitForSilence();},
        generateNow:value=>submit(true,typeof value==='string'?value:undefined),
        cancel() {clear();revision++;active=false;speaking=false;text='';interim='';status='idle';message='';notify();},
        pause() {if(!active || disposed)return;clear();revision++;active=false;speaking=false;status=exampleWordCount(preview())>=minWords?'ready':'needs-detail';message='Voice paused. Review the captured explanation before generating.';notify();},
        dispose() {clear();disposed=true;active=false;revision++;},
    };
}
